import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AppPath } from '@app/app-path';
import { CassError } from '@core/models/cass-error';
import { UserService } from '@core/services/user.service';
import { LoadingOverlayService } from '@shared/services/loading-overlay.service';
import { PasswordService } from '@shared/services/password.service';
import { UserAlertService } from '@shared/services/user-alert.service';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';
import { SubSink } from 'subsink';

@Component({
	selector: 'cass-forgot-password-page',
	templateUrl: './forgot-password-page.component.html',
	styleUrls: ['./forgot-password-page.component.css']
})
export class ForgotPasswordPageComponent implements OnDestroy {

	emailVerificationRequired: boolean = true;
	passwordChangeRequired: boolean = true;
	passwordChanged: boolean = false;

	enterEmailForm: FormGroup;
	changePasswordForm: FormGroup;

	protected isPasswordShown = false;

	private _callingOut$ = new BehaviorSubject<boolean>(false);
	private _subs = new SubSink();

	constructor (
		private _userService: UserService,
		private _alertService: UserAlertService,
		private _router: Router,
		private _passwordService: PasswordService,
		private _loadingOverlayService: LoadingOverlayService
	) {
		this.enterEmailForm = new FormGroup({
			"email": new FormControl<string>("", [Validators.required, Validators.email, Validators.maxLength(254)])
		});

		this.changePasswordForm = new FormGroup({
			"verificationCode": new FormControl<string>("", [Validators.required]),
			"newPassword": new FormControl < string >("", [Validators.required, Validators.minLength(this._passwordService.MIN_PASS_LENGTH)]),
			"confirmPassword": new FormControl < string >("", [Validators.required, Validators.minLength(this._passwordService.MIN_PASS_LENGTH)])
		});

		// add match passwords validator
		this.changePasswordForm.controls["confirmPassword"].addValidators(
			this._passwordService.confirmPasswordValidator(this.changePasswordForm.controls["newPassword"])
		);

		this._subs.add(
			this._callingOut$
				.pipe(
					distinctUntilChanged()
				)
				.subscribe((isCallingOut: boolean) => {
					if (isCallingOut) {
						this._loadingOverlayService.showIndeterminateHorizontalProgressIndicator();
					}
					else {
						this._loadingOverlayService.hide();
					}
				}));

	}

	ngOnDestroy(): void {
		this._subs.unsubscribe();
	}

	sendVerificationEmail() {
		if (!this.enterEmailForm.valid) {
			// trigger display of all field validation errors
			this.enterEmailForm.markAllAsTouched();
			return;
		}

		const emailAddress = this.enterEmailForm.value["email"] || "";
		this._callingOut$.next(true);
		this._userService.forgotPassword(emailAddress).subscribe({
			next: () => {
				this._callingOut$.next(false);
				this.emailVerificationRequired = false;
			},
			error: (error: CassError) => {
				this._callingOut$.next(false);
				this._alertService.errorByCassError(error);
			}
		});
	}

	sendChangePasswordRequest() {
		if (!this.changePasswordForm.valid) {
			// trigger display of all field validation errors
			this.changePasswordForm.markAllAsTouched();
			return;
		}

		const email = this.enterEmailForm.get("email")?.value;
		const verificationCode = this.changePasswordForm.get("verificationCode")?.value;
		const password = this.changePasswordForm.get("newPassword")?.value;

		this._callingOut$.next(true);
		this._userService.resetPassword(email, verificationCode, password).subscribe({
			next: () => {
				this._callingOut$.next(false);
				this.passwordChangeRequired = false;
				this.passwordChanged = true;
			},
			error: (error: CassError) => {
				this._callingOut$.next(false);
				this._alertService.errorByCassError(error);
			}
		})
	}

	goToLoginPage(): void {
		this._router.navigate([AppPath.Login]);
	}
}
